/**
 * =========================================================================================
 *     Copyright © 2025 Dobler Data Solutions, LLC. ALL RIGHTS RESERVED.
 * =========================================================================================
 *     File            :   LakehouseHealthTable.tsx
 *     Description     :   Sortable, filterable table of lakehouse health data
 *     Author          :   Peter Dobler
 *     Created         :   2026-01-19
 *     Modified        :   2026-01-19 | Peter Dobler | Initial implementation
 *     Constitution    :   v1.11.0 (Specification Testing Standards Edition)
 *     Feature         :   028-lakehouse-maintenance
 * =========================================================================================
 */

import React, { useState } from 'react';
import axios from 'axios';
import { ChevronDown, ChevronUp, RefreshCw } from 'lucide-react';
import { Card, Badge, Spinner, Input, Select, AppTooltip, EmptyState, ErrorState } from '../ui';
import type { BadgeProps } from '../ui';
import { DualScrollTable, PaginationControls } from '../common/TableControls';
import { useLakehousesWithHealth } from '../../hooks/useMaintenanceData';
import type { LakehouseWithHealth } from '../../services/maintenanceApi';
import MaintenanceHistoryList from './MaintenanceHistoryList';
import { formatEastern } from '@/utils/dateFormatter';

type SortField = 'name' | 'healthScore' | 'lastHealthCheck' | 'capacityType';
type HealthStatusFilter = 'Healthy' | 'Degraded' | 'Unhealthy' | '';

const healthStatusColors: Record<'Healthy' | 'Degraded' | 'Unhealthy', BadgeProps['variant']> = {
  Healthy: 'success',
  Degraded: 'warning',
  Unhealthy: 'error',
};

const iconBtn =
  'inline-flex items-center justify-center rounded-[var(--radius-md)] p-1.5 text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-subtle)] hover:text-[var(--color-text-primary)]';

export const LakehouseHealthTable: React.FC = () => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [sortBy, setSortBy] = useState<SortField>('healthScore');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [expandedRow, setExpandedRow] = useState<string | null>(null);
  const [healthFilter, setHealthFilter] = useState<HealthStatusFilter>('');
  const [capacityFilter, setCapacityFilter] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState<string>('');

  const { data, isLoading, error, refetch } = useLakehousesWithHealth(
    page + 1,
    rowsPerPage,
    {
      healthStatus: healthFilter || undefined,
      capacityType: capacityFilter || undefined,
      search: searchQuery || undefined,
    },
    sortBy,
    sortOrder
  );

  const handleSort = (field: SortField) => {
    const isAsc = sortBy === field && sortOrder === 'asc';
    setSortOrder(isAsc ? 'desc' : 'asc');
    setSortBy(field);
  };

  const handleExpandClick = (lakehouseId: string) => {
    setExpandedRow(expandedRow === lakehouseId ? null : lakehouseId);
  };

  const total = data?.total || 0;
  const shown = data?.data?.length || 0;
  const totalPages = Math.max(1, Math.ceil(total / rowsPerPage));

  // Token-styled sortable header cell (server-side sort via handleSort).
  const SortHeader: React.FC<{ field: SortField; children: React.ReactNode }> = ({
    field,
    children,
  }) => {
    const active = sortBy === field;
    return (
      <button
        type="button"
        onClick={() => handleSort(field)}
        className="inline-flex select-none items-center gap-1 transition-colors hover:text-[var(--color-accent)]"
      >
        <span>{children}</span>
        {active &&
          (sortOrder === 'asc' ? (
            <ChevronUp className="h-3.5 w-3.5 text-[var(--color-accent)]" />
          ) : (
            <ChevronDown className="h-3.5 w-3.5 text-[var(--color-accent)]" />
          ))}
      </button>
    );
  };

  const thClass = 'px-3 py-2 text-left font-semibold text-[var(--color-text-primary)]';

  // Spec 298 FR-006 (owner decision, Session 2026-11-04): GET /api/v1/maintenance/lakehouses answers 501, since no
  // health service exists (backend maintenanceRoutes.ts). Say so, instead of "No lakehouses found" over a stub.
  if (axios.isAxiosError(error) && error.response?.status === 501) {
    return (
      <Card>
        <EmptyState
          title="Lakehouse health is not implemented yet"
          message="No service collects lakehouse health, so there is no list to show."
        />
      </Card>
    );
  }

  return (
    <Card padding="none" className="overflow-hidden">
      {/* Filters */}
      <div className="flex items-end gap-4 p-4">
        <Input
          label="Search"
          inputSize="sm"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="w-full max-w-[300px]"
        />
        <Select
          label="Health Status"
          selectSize="sm"
          value={healthFilter}
          onChange={(e) => setHealthFilter(e.target.value as HealthStatusFilter)}
          className="min-w-[150px]"
        >
          <option value="">All</option>
          <option value="Healthy">Healthy</option>
          <option value="Degraded">Degraded</option>
          <option value="Unhealthy">Unhealthy</option>
        </Select>
        <Select
          label="Capacity Type"
          selectSize="sm"
          value={capacityFilter}
          onChange={(e) => setCapacityFilter(e.target.value)}
          className="min-w-[150px]"
        >
          <option value="">All</option>
          <option value="Reference">Reference</option>
          <option value="ClientDedicated">Client Dedicated</option>
          <option value="MultiTenant">MultiTenant</option>
          <option value="Platform">Platform</option>
        </Select>
        <AppTooltip title="Refresh">
          <button className={iconBtn} onClick={() => refetch()} aria-label="Refresh">
            <RefreshCw className="h-4 w-4" />
          </button>
        </AppTooltip>
      </div>

      {/* Table */}
      <DualScrollTable>
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr className="border-b-2 border-[var(--color-border)] bg-[var(--color-bg-subtle)]">
              <th className="w-[50px] px-3 py-2" />
              <th className={thClass}>
                <SortHeader field="name">Lakehouse Name</SortHeader>
              </th>
              <th className={thClass}>
                <SortHeader field="capacityType">Capacity Type</SortHeader>
              </th>
              <th className={thClass}>Client</th>
              <th className={thClass}>
                <SortHeader field="healthScore">Health Score</SortHeader>
              </th>
              <th className={thClass}>Status</th>
              <th className={thClass}>
                <SortHeader field="lastHealthCheck">Last Check</SortHeader>
              </th>
            </tr>
          </thead>
          <tbody>
            {isLoading || (!data && !error) ? (
              <tr>
                <td colSpan={7} className="py-8 text-center">
                  <Spinner />
                </td>
              </tr>
            ) : !data ? (
              <tr>
                <td colSpan={7} className="p-4">
                  <ErrorState title="Could not read lakehouse health" onRetry={() => refetch()} />
                </td>
              </tr>
            ) : data.data?.length === 0 ? (
              <tr>
                <td colSpan={7} className="py-8 text-center">
                  <span className="text-sm text-[var(--color-text-secondary)]">
                    No lakehouses found
                  </span>
                </td>
              </tr>
            ) : (
              data?.data?.map((lakehouse: LakehouseWithHealth) => (
                <React.Fragment key={lakehouse.id}>
                  <tr className="border-b border-[var(--color-border)] hover:bg-[var(--color-bg-subtle)]">
                    <td className="px-3 py-2">
                      <button
                        className={iconBtn}
                        onClick={() => handleExpandClick(lakehouse.id)}
                        aria-label={expandedRow === lakehouse.id ? 'Collapse' : 'Expand'}
                      >
                        {expandedRow === lakehouse.id ? (
                          <ChevronUp className="h-4 w-4" />
                        ) : (
                          <ChevronDown className="h-4 w-4" />
                        )}
                      </button>
                    </td>
                    <td className="px-3 py-2">
                      <span className="text-sm font-medium text-[var(--color-text-primary)]">
                        {lakehouse.name}
                      </span>
                    </td>
                    <td className="px-3 py-2">
                      <Badge variant="default" isOutlined>
                        {lakehouse.capacityType}
                      </Badge>
                    </td>
                    <td className="px-3 py-2 text-[var(--color-text-primary)]">
                      {lakehouse.clientName || <em>N/A</em>}
                    </td>
                    <td className="px-3 py-2">
                      <span className="text-sm font-semibold text-[var(--color-text-primary)]">
                        {lakehouse.healthScore}
                      </span>
                    </td>
                    <td className="px-3 py-2">
                      <Badge variant={healthStatusColors[lakehouse.healthStatus]}>
                        {lakehouse.healthStatus}
                      </Badge>
                    </td>
                    <td className="px-3 py-2">
                      <span className="text-sm text-[var(--color-text-secondary)]">
                        {formatEastern(lakehouse.lastHealthCheck)}
                      </span>
                    </td>
                  </tr>
                  {expandedRow === lakehouse.id && (
                    <tr>
                      <td colSpan={7} className="p-0">
                        <div className="m-4">
                          <h3 className="mb-2 text-base font-semibold text-[var(--color-text-primary)]">
                            Maintenance History
                          </h3>
                          <MaintenanceHistoryList lakehouseId={lakehouse.id} compact />
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))
            )}
          </tbody>
        </table>
      </DualScrollTable>

      {/* Pagination — server-side; map 0-indexed page state to 1-indexed controls. */}
      <PaginationControls
        page={page + 1}
        pageSize={rowsPerPage}
        total={total}
        totalPages={totalPages}
        start={page * rowsPerPage}
        shown={shown}
        onPage={(p) => setPage(p - 1)}
        onPageSize={(s) => {
          setRowsPerPage(s);
          setPage(0);
        }}
      />
    </Card>
  );
};

export default LakehouseHealthTable;
